import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import type { ComponentProps, ReactNode } from "react";
import { PhoneInput } from "@/components/ui/input/phone-input";
import {
  type FieldBindingProps,
  PolyField,
  splitBinding,
} from "@/fields/_internal/poly-field";

type PhoneFieldBaseProps = Omit<
  ComponentProps<typeof PhoneInput>,
  "value" | "onChange" | "onBlur" | "onClear" | "className"
> & {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner PhoneInput. */
  inputClassName?: string;
  /** Called after the field's own handleBlur. */
  onBlur?: () => void;
  /** Fired AFTER the value is committed — for side effects only. */
  onValueChange?: (value: string) => void;
};

export type PhoneFieldProps = PhoneFieldBaseProps & FieldBindingProps;

export function PhoneField(props: PhoneFieldProps) {
  const [binding, rest] = splitBinding(props);
  const {
    label,
    description,
    optional,
    className,
    inputClassName,
    onBlur,
    onValueChange,
    ...inputProps
  } = rest;
  return (
    <PolyField<string> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={inputProps.disabled}
        >
          <PhoneInput
            {...inputProps}
            className={inputClassName}
            value={field.state.value ?? ""}
            onChange={(e) => {
              const next = e.target.value.trim();
              field.handleChange(next);
              onValueChange?.(next);
            }}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
            onClear={() => field.handleChange("")}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
